import { Injectable, inject } from '@angular/core';

import { RuntimeConfigService } from '../common/runtime-config.service';

/**
 * Carries a single, FULLY COMPOSED toast line (sentence + diagnostic code)
 * across a redirect to the login page, so it can show it there.
 *
 * ## Why this exists
 *
 * `AUTH` (a 401) is classified at the call site that was loading a sheet or a
 * page — `SheetLoadTraceService` or `buildPageLoadFailureNotice` — but that
 * call site is never where the user ends up: `sessionExpiryInterceptor` has
 * already called `AuthService.forceLogout(reason)` and redirected to
 * `/auth/login` by the time anything could render there, and
 * `AuthLayoutComponent`/`LoginComponent` are a completely separate layout tree
 * with no `MessageService`/`<p-toast>` wired into the page that failed.
 *
 * A composed line has to travel somewhere between "classified" and "shown".
 * This is that somewhere: an in-memory, root-provided slot, `stage()`d by
 * whichever mechanism classified the failure and `consume()`d exactly once by
 * `LoginComponent`.
 *
 * ## Why a composed SUMMARY, not a bare code (generalised 2026-10-05)
 *
 * This started as "carries an AUTH code"; `LoginComponent` reconstructed the
 * sentence itself via a hardcoded `LOAD_FAILURE_COPY.AUTH`. That broke the
 * moment a NON-AUTH failure needed the same landing spot:
 * `isAuthenticatedGuard`'s own network/CSRF failure — it could not even
 * confirm whether a session exists, so there is no mounted page anywhere to
 * toast on, and the login page is the only page a guard failure can safely
 * route to either way. Staging that failure's bare code would have made the
 * login page show "Your session has expired..." for what might be a plain
 * network outage. Every caller therefore stages the notice's already-composed
 * `.summary` (from `buildPageLoadFailureNotice`, or the equivalent inline
 * composition `SheetLoadTraceService`/`AuthService.forceLogout` build for
 * their own AUTH-silent branches) — this service never assumes a class.
 *
 * ## Why in-memory rather than `history.state` (like `authReason`)
 *
 * `AuthService.forceLogout`'s own navigation state marker (`authReason`) is
 * set by the INTERCEPTOR, synchronously, before the failing request's error
 * even reaches the component-level `catchError`/subscribe that classifies
 * the failure and composes its summary. By the time a summary exists, the
 * interceptor's `router.navigate(...)` call has already been made with its
 * `state` object fixed — there is nothing left to attach one to. A plain
 * in-memory field written moments later, before the router's async
 * navigation finishes swapping in `LoginComponent`, has no such ordering
 * problem.
 *
 * ## Why read-once
 *
 * Without consuming on read, a notice staged by one failed page-load would
 * keep reappearing on every later, unrelated visit to `/auth/login` within
 * the same JS context (e.g. after a manual sign-out and a fresh sign-in
 * attempt).
 */
@Injectable({ providedIn: 'root' })
export class AuthFailureNoticeService {
  // REFACTORED-DIAGNOSTIC-CODES-FLAG: remove with the flag once production is cleared.
  private readonly runtimeConfig = inject(RuntimeConfigService);
  private pendingSummary: string | null = null;

  /**
   * Record the composed toast line for the next thing that reads it. Overwrites silently — only the latest failure matters.
   *
   * REFACTORED-DIAGNOSTIC-CODES-FLAG: a no-op while `refactoredDiagnosticCodesEnabled`
   * is `false` (production). Before the coded diagnostics existed the login page
   * showed nothing after a forced logout, and this is the ONE choke point every
   * AUTH staging site funnels through (sheet and page loads, the auth guard, the
   * `MBRS-AUTH` fallback in `forceLogout`), so gating it here keeps all
   * of them silent without touching each caller.
   */
  stage(summary: string): void {
    if (!this.runtimeConfig.refactoredDiagnosticCodesEnabled()) {
      return;
    }
    this.pendingSummary = summary;
  }

  /** Read and clear in one step, so a later unrelated visit to the login page sees nothing. */
  consume(): string | null {
    const summary = this.pendingSummary;
    this.pendingSummary = null;
    return summary;
  }
}
